import Select from "@mui/material/Select";
import type { SelectProps } from "@mui/material/Select";
import Tooltip from "@mui/material/Tooltip";
import type { TooltipProps } from "@mui/material/Tooltip";
import { useState } from "react";

export type SelectWithTooltipProps<Value = unknown> = SelectProps<Value> & {
  tooltipTitle: TooltipProps["title"];
  tooltipProps?: Omit<TooltipProps, "children" | "open" | "title" | "onOpen" | "onClose">;
};

/**
 * A Select with a descriptive tooltip that is automatically hidden while its menu is open.
 * Coordinates both controlled and uncontrolled Select state so menu content is never obscured
 * by the tooltip.
 */
export function SelectWithTooltip<Value = unknown>({
  tooltipTitle,
  tooltipProps,
  open,
  onOpen,
  onClose,
  ...selectProps
}: SelectWithTooltipProps<Value>) {
  const [isInternallyOpen, setIsInternallyOpen] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const isSelectOpen = open ?? isInternallyOpen;

  const handleOpen: NonNullable<SelectProps<Value>["onOpen"]> = (event) => {
    setIsInternallyOpen(true);
    setIsTooltipOpen(false);
    onOpen?.(event);
  };

  const handleClose: NonNullable<SelectProps<Value>["onClose"]> = (event) => {
    setIsInternallyOpen(false);
    onClose?.(event);
  };

  return (
    <Tooltip
      {...tooltipProps}
      title={tooltipTitle}
      open={isTooltipOpen && !isSelectOpen}
      onOpen={() => {
        if (!isSelectOpen) setIsTooltipOpen(true);
      }}
      onClose={() => setIsTooltipOpen(false)}
    >
      <Select {...selectProps} open={open} onOpen={handleOpen} onClose={handleClose} />
    </Tooltip>
  );
}
